import React, { useEffect, useState } from "react";
import { fmtDate } from "../lib/format";
import { Plus, X } from "lucide-react";

// Paper position tracker + open-position form for the backtest replay.
export const BacktestPositions = ({ session, loadChain, selectedId, onSelect, onOpen, onClose }) => {
  const [sym, setSym] = useState(session.symbols[0] || "");
  const [chain, setChain] = useState(null);
  const [exp, setExp] = useState("");
  const [strike, setStrike] = useState("");
  const [contracts, setContracts] = useState(1);

  useEffect(() => {
    if (!sym) return;
    let alive = true;
    setChain(null);
    loadChain(sym).then((c) => {
      if (!alive) return;
      setChain(c);
      setExp("");
      setStrike("");
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym, session.current_ts]);

  const expirations = chain ? [...new Set(chain.options.map((o) => o.expiration))].sort() : [];
  const strikes = chain && exp ? chain.options.filter((o) => o.expiration === exp).sort((a, b) => a.strike - b.strike) : [];
  const pickedOpt = strikes.find((o) => String(o.strike) === strike);

  const submit = () => {
    if (!sym || !exp || !pickedOpt) return;
    onOpen({ symbol: sym, strike: pickedOpt.strike, expiration: exp, contracts: Number(contracts) || 1 });
  };

  return (
    <div className="panel" data-testid="bt-positions-panel">
      <div className="panel-h">
        <div className="label-cap">Paper Positions</div>
        <div className="text-[10px] font-mono text-[#BF5AF2]">REPLAY</div>
      </div>

      <div className="p-3 space-y-1.5">
        {session.positions.length === 0 && (
          <p className="text-[11px] text-[#71717A] py-2">No paper positions yet — open one below.</p>
        )}
        {session.positions.map((p) => (
          <div
            key={p.id}
            data-testid={`bt-position-${p.symbol}-${p.strike}`}
            onClick={() => onSelect(p.id)}
            className={`flex items-center justify-between px-2 py-2 cursor-pointer border transition-colors ${
              selectedId === p.id ? "border-[#BF5AF2] bg-[rgba(191,90,242,0.08)]" : "border-[#27272A] hover:bg-[#121212]"
            }`}
          >
            <div className="font-mono text-xs">
              <span className="text-white">{p.symbol}</span>{" "}
              <span className="text-[#007AFF]">${p.strike}C</span>{" "}
              <span className="text-[#A1A1AA]">{fmtDate(p.expiration)}</span>
              <div className="text-[10px] text-[#71717A]">
                ×{p.contracts} · sold @ ${Number(p.premium_received).toFixed(2)}
              </div>
            </div>
            <button
              data-testid={`bt-close-position-${p.id}`}
              onClick={(e) => { e.stopPropagation(); onClose(p.id); }}
              className="text-[#71717A] hover:text-[#FF3B30] p-1"
              title="Buy back / close"
            >
              <X size={13} />
            </button>
          </div>
        ))}
      </div>

      <div className="px-3 pb-3 pt-2 border-t border-[#27272A] space-y-2">
        <div className="label-cap">Open Paper Call</div>
        <select data-testid="bt-open-symbol" value={sym} onChange={(e) => setSym(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none">
          {session.symbols.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="bt-open-expiration" value={exp} onChange={(e) => { setExp(e.target.value); setStrike(""); }} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none" disabled={!chain}>
          <option value="">{chain ? "Expiration…" : "Loading chain…"}</option>
          {expirations.map((e) => <option key={e} value={e}>{fmtDate(e)}</option>)}
        </select>
        <select data-testid="bt-open-strike" value={strike} onChange={(e) => setStrike(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none" disabled={!exp}>
          <option value="">Strike…</option>
          {strikes.map((o) => (
            <option key={o.strike} value={o.strike}>
              ${o.strike} · bid ${o.bid.toFixed(2)} · Δ{o.delta.toFixed(2)}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            data-testid="bt-open-contracts"
            type="number" min="1" value={contracts}
            onChange={(e) => setContracts(e.target.value)}
            className="w-20 bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none"
          />
          <button
            data-testid="bt-open-submit"
            onClick={submit}
            disabled={!pickedOpt}
            className="btn-accent flex-1 text-xs py-2 flex items-center justify-center gap-1.5 disabled:opacity-40"
          >
            <Plus size={13} /> Sell to open{pickedOpt ? ` @ $${pickedOpt.bid.toFixed(2)}` : ""}
          </button>
        </div>
        {chain && (
          <p className="text-[10px] text-[#71717A]">
            {sym} @ ${chain.underlying_price?.toFixed(2)} · fills: sell at bid, buy at ask
          </p>
        )}
      </div>
    </div>
  );
};
